import axios from "axios";

// ═════════════════════════════════════════════════════════════════════
//  DEMO DATA — for a customer on the FREE plan.
//  The Free plan shows DEMO data only: the pages look and behave like the real thing (a company, three stores, orders over
//  the last 30 days, customers with their order history …) but none of it is real. It is made right here in the browser:
//  while the mode is on, the few data requests of the customer Dashboard and Customers pages are answered with this data
//  instead of going to the server (an axios adapter, installed by CustomerPlanSync when the plan is the Free plan, removed
//  as soon as it is not). Every other request goes to the server as usual.
// ═════════════════════════════════════════════════════════════════════

// small seeded random number generator — the same demo every time, so numbers do not jump between refreshes
const rng = (seed) => () => {
  seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
const pick = (rand, arr) => arr[Math.floor(rand() * arr.length)];
const round2 = (n) => Math.round(n * 100) / 100;

const COMPANY = { id: "demo-company", name: "Demo Company" };
const STORES = [
  { store_id: "900001", store_name: "Aurora Home Decor", store_domain: "aurora-home.shop" },
  { store_id: "900002", store_name: "Nordic Pet Supply", store_domain: "nordicpet.store" },
  { store_id: "900003", store_name: "Peak Fitness Gear", store_domain: "peakfitgear.shop" },
];
const PRODUCTS = [
  "Minimalist Ceramic Vase Set", "Memory Foam Pet Bed", "Resistance Band Kit (5 pcs)", "LED Sunset Projection Lamp", "Bamboo Cutting Board",
  "Stainless Steel Water Bottle", "Adjustable Dumbbell Pair", "Cozy Knit Throw Blanket", "Automatic Pet Water Fountain", "Yoga Mat Non-Slip Pro",
  "Scented Soy Candle Trio", "Portable Blender 380ml", "Wall Floating Shelves (3 pcs)", "Dog Chew Toy Bundle", "Foam Roller Deep Tissue",
];
const FIRST = ["Olivia", "Liam", "Emma", "Noah", "Ava", "Elijah", "Sophia", "James", "Isabella", "Mason", "Mia", "Lucas", "Amelia", "Ethan", "Harper", "Logan", "Evelyn", "Jack", "Abigail", "Henry", "Ella", "Owen", "Scarlett", "Daniel"];
const LAST = ["Johnson", "Smith", "Brown", "Davis", "Miller", "Wilson", "Moore", "Taylor", "Anderson", "Thomas", "Jackson", "White", "Harris", "Martin", "Thompson", "Garcia", "Martinez", "Robinson", "Clark", "Lewis"];
const PLACES = [
  ["United States", "California", "Los Angeles", "90012"], ["United States", "Texas", "Austin", "73301"], ["United States", "New York", "Brooklyn", "11201"],
  ["United States", "Florida", "Orlando", "32801"], ["United States", "Washington", "Seattle", "98101"], ["United States", "Illinois", "Chicago", "60601"],
  ["Canada", "Ontario", "Toronto", "M5V 2T6"], ["United Kingdom", "England", "Manchester", "M1 1AE"],
];
const STREETS = ["Maple Street", "Oak Avenue", "Pine Road", "Cedar Lane", "Sunset Boulevard", "Lakeview Drive", "Highland Park Way", "River Street"];

let cache = null;
const build = () => {
  const dayKey = new Date().toISOString().slice(0, 10);
  if (cache && cache.dayKey === dayKey) return cache;
  const rand = rng(20260101);

  // people first, so the same customer can have several orders
  const people = Array.from({ length: 24 }, (_, i) => {
    const first = FIRST[i % FIRST.length]; const last = pick(rand, LAST);
    const [country, state, city, postalCode] = pick(rand, PLACES);
    return {
      key: `demo-cust-${i + 1}`, name: `${first} ${last}`, email: `${first}.${last}${10 + i}@example.com`.toLowerCase(),
      phone: `+1 (${200 + Math.floor(rand() * 700)}) ${100 + Math.floor(rand() * 800)}-${1000 + Math.floor(rand() * 8999)}`,
      company: "", country, state, city, postalCode,
      address: `${100 + Math.floor(rand() * 8800)} ${pick(rand, STREETS)}`, address2: rand() > 0.8 ? `Apt ${1 + Math.floor(rand() * 40)}` : "",
    };
  });

  const orders = [];
  const N = 150;
  for (let i = 0; i < N; i += 1) {
    // more orders on recent days: the day index leans towards 0
    const daysAgo = Math.floor(Math.pow(rand(), 1.7) * 30);
    const when = new Date(Date.now() - daysAgo * 86400000 - Math.floor(rand() * 20) * 3600000 - Math.floor(rand() * 59) * 60000);
    const store = STORES[i % STORES.length];
    const person = people[Math.floor(rand() * people.length)];
    const qty = rand() > 0.82 ? 2 : 1;
    const amount = round2((22 + rand() * 120) * qty);
    const cost = round2(amount * (0.42 + rand() * 0.16));
    const fee = round2(amount * 0.03);
    const referral = round2(amount * 0.19);
    const profit = round2(amount - cost - fee);
    const roll = rand();
    const status = roll < 0.07 ? "cancelled" : roll < 0.22 ? "pending" : "paid";
    const fulfillment = status === "cancelled" ? "abandoned" : status === "pending" ? "not_processed" : pick(rand, ["delivered", "delivered", "shipped", "processing"]);
    orders.push({
      order_id: 5000100 + i, order_hash: `DEMO-${String(48210 + i * 7).padStart(6, "0")}`, store_id: store.store_id, store_domain: store.store_domain,
      store_name: store.store_name, order_date: when.toISOString(), date: when.toISOString(), amount, cost, fee, profit, referral_fee: referral,
      status, fulfillment, action: {}, customer_name: person.name, customer_email: person.email, customer_key: person.key,
      products: [{ title: pick(rand, PRODUCTS), quantity: qty, price: round2(amount / qty) }],
    });
  }
  orders.sort((a, b) => new Date(b.order_date) - new Date(a.order_date));
  cache = { dayKey, people, orders };
  return cache;
};

const summaryOf = (list) => {
  const sum = (k) => round2(list.reduce((s, o) => s + Number(o[k] || 0), 0));
  const processed = list.filter((o) => o.status === "paid" && ["delivered", "shipped"].includes(o.fulfillment)).length;
  const cancelled = list.filter((o) => o.status === "cancelled").length;
  return {
    totalRevenue: sum("amount"), totalProfit: sum("profit"), totalFee: sum("fee"), totalCost: sum("cost"), totalReferralFee: sum("referral_fee"),
    statusCounts: { processed, cancelled, pending: list.length - processed - cancelled },
  };
};

// ── the answers ──
const q = (config) => {
  const out = {};
  try {
    const u = new URL(config.url, "http://x");
    u.searchParams.forEach((v, k) => { out[k] = v; });
  } catch { /* no query */ }
  Object.assign(out, config.params || {});
  return out;
};

const answerOrders = (config) => {
  const { orders } = build();
  const p = q(config);
  let list = orders;
  if (p.date_from) list = list.filter((o) => new Date(o.order_date) >= new Date(p.date_from));
  if (p.date_to) list = list.filter((o) => new Date(o.order_date) <= new Date(p.date_to));
  if (p.store_ids) { const ids = new Set(String(p.store_ids).split(",")); list = list.filter((o) => ids.has(String(o.store_id))); }
  const limit = Math.max(1, Number(p.limit) || 100);
  const page = Math.max(1, Number(p.page) || 1);
  return { success: true, orders: list.slice((page - 1) * limit, page * limit), summary: summaryOf(list), totalOrders: list.length, currentPage: page, totalPages: Math.ceil(list.length / limit) };
};

const storeRow = (s) => ({ ...s, sellvia_dashboard_id: COMPANY.id, is_active: true, thumbnail_url: "", value: 1 });

const answerCustomers = (config) => {
  const { people, orders } = build();
  const p = q(config);
  const term = String(p.search || "").trim().toLowerCase();
  let rows = people.map((c) => {
    const mine = orders.filter((o) => o.customer_key === c.key).map((o) => ({ order_id: o.order_id, order_hash: o.order_hash, order_date: o.order_date, amount: o.amount, status: o.status, store_domain: o.store_domain }));
    return { ...c, orders: mine, orderCount: mine.length };
  }).filter((c) => c.orderCount > 0);
  if (term) rows = rows.filter((c) => [c.name, c.email, c.phone].some((v) => String(v).toLowerCase().includes(term)));
  const limit = Math.max(1, Number(p.limit) || 20);
  const page = Math.max(1, Number(p.page) || 1);
  return { success: true, customers: rows.slice((page - 1) * limit, page * limit), totalCustomers: rows.length, totalOrders: rows.reduce((s, c) => s + c.orderCount, 0), currentPage: page, totalPages: Math.ceil(rows.length / limit) };
};

const ROUTES = [
  [/\/api\/sellvia\/orders(\?|$)/, answerOrders],
  [/\/api\/sellvia\/customers(\?|$)/, answerCustomers],
  [/\/api\/sellvia\/top-products(\?|$)/, () => ({ success: true, products: [] })],
  [/\/api\/sellvia\/stores(\?|$)/, () => ({ success: true, stores: STORES.map(storeRow) })],
  [/\/api\/sellvia\/store-names(\?|$)/, () => ({ success: true, companies: [{ company_id: COMPANY.id, company_name: COMPANY.name, stores: STORES.map(storeRow) }] })],
  [/\/api\/sellvia\/store-order-counts(\?|$)/, () => {
    const { orders } = build(); const counts = {}; const todayCounts = {}; const today = new Date().toDateString();
    orders.forEach((o) => { counts[o.store_id] = (counts[o.store_id] || 0) + 1; if (new Date(o.order_date).toDateString() === today) todayCounts[o.store_id] = (todayCounts[o.store_id] || 0) + 1; });
    return { success: true, counts, todayCounts };
  }],
  [/\/api\/sellvia\/dashboards(\?|$)/, () => ({ success: true, dashboards: [{ _id: COMPANY.id, id: COMPANY.id, dashboard_name: COMPANY.name, name: COMPANY.name, total_stores: STORES.length }] })],
  [/\/api\/company-stores(\?|$)/, () => ({ success: true, companies: [{ _id: COMPANY.id, name: COMPANY.name, stores: STORES.map(storeRow) }] })],
];

const match = (config) => {
  if (String(config.method || "get").toLowerCase() !== "get") return null;
  const url = String(config.url || "");
  const hit = ROUTES.find(([re]) => re.test(url));
  return hit ? hit[1](config) : null;
};

// ── on / off ──
let originalAdapter = null;
export const demoDataOn = () => originalAdapter !== null;

export const enableDemoData = () => {
  if (originalAdapter) return;
  originalAdapter = axios.defaults.adapter;
  const real = axios.getAdapter ? axios.getAdapter(originalAdapter) : originalAdapter;
  axios.defaults.adapter = (config) => {
    const data = match(config);
    if (data) return Promise.resolve({ data, status: 200, statusText: "OK", headers: {}, config, request: {} });
    return real(config);
  };
};

export const disableDemoData = () => {
  if (!originalAdapter) return;
  axios.defaults.adapter = originalAdapter;
  originalAdapter = null;
};
